import { FC, useState } from 'react'
import Modal from '@mui/material/Modal'
import Button from '@mui/material/Button'
import { styled } from '@mui/material/styles'
import {
  Typography,
  Divider,
  TextField,
  Box,
  TextFieldProps,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import CloseIcon from '@mui/icons-material/Close'

interface OnHoldTicketModalProps {
  open: boolean
  onClose: () => void
  onHold: (ticketId: number, note: string) => void
  data?: any
  title: string
}

// Styled Components (unchanged)
const StyledModal = styled(Modal)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const ModalContent = styled(Box)({
  width: 600,
  height: 600,
  overflow: 'auto',
  backgroundColor: '#ffffff',
  borderRadius: 12,
  boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.1)',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  boxSizing: 'border-box',
})

const ModalTitle = styled(Typography)({
  fontSize: '22px',
  fontWeight: 500,
  fontFamily: 'Gabarito',
  color: '#505470',
  marginLeft: 1,
})

const CustomTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderWidth: 2,
    },
    '&:hover fieldset': {
      borderWidth: 2,
    },
    '&.Mui-focused fieldset': {
      borderWidth: 2,
    },
  },
})

const ActionButton = styled(Button)({
  textTransform: 'none',
  borderRadius: 8,
  width: 187,
  height: 47,
  minWidth: 187,
  minHeight: 47,
  fontSize: '18px',
  fontFamily: 'Gabarito',
  fontWeight: 600,
})

const ButtonGroup = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: 20,
  marginBottom: 20,
  marginRight: 15,
})

const ModalBody = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
})

export const OnHoldTicketModal: FC<OnHoldTicketModalProps> = ({
  open,
  onClose,
  onHold,
  data,
  title,
}) => {
  const [remarks, setRemarks] = useState('')
  return (
    <StyledModal open={open} onClose={onClose}>
      <ModalContent>
        {/* Modal Header and Divider */}
        <Box display="flex" flexDirection="column" justifyContent="flex-start">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            marginLeft={2}
          >
            <ModalTitle>{`${title}`}</ModalTitle>
            <Button
              onClick={onClose}
              style={{ color: '#000000' }}
              sx={{ width: 20, height: 20 }}
            >
              <CloseIcon />
            </Button>
          </Box>
          <Divider
            sx={{
              color: '#B1B8BF',
              margin: '8px 0',
              borderBottomWidth: '2.5px',
            }}
          />
        </Box>

        {/* Modal Body */}
        <ModalBody sx={{ marginBottom: 3 }}>
          <Typography sx={{ marginTop: 6 }}>
            This will on hold the ticket selected.
          </Typography>

          {/* Remarks Input */}
          <Box marginBottom={2}>
            <Typography sx={{ marginBottom: 1 }}>
              Remarks
              <span style={{ color: 'red' }}>*</span>
            </Typography>
            <CustomTextField
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              fullWidth
              multiline
              maxRows={3}
              variant="outlined"
              placeholder="Enter remarks here..."
              required
              helperText={remarks === '' ? 'This field is required' : ''}
              error={remarks === ''}
            />
          </Box>
        </ModalBody>

        {/* Action Buttons */}
        <ButtonGroup>
          <ActionButton
            variant="outlined"
            sx={{
              borderColor: 'black',
              color: 'black',
              '&:hover': {
                borderColor: 'black',
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
              },
            }}
            onClick={onClose}
          >
            Cancel
          </ActionButton>
          <ActionButton
            variant="contained"
            sx={{
              marginLeft: 2,
              backgroundColor: '#379F86',
              color: '#ffffff',
            }}
            onClick={() => onHold(data.id, remarks)}
            disabled={remarks === ''}
          >
            Submit
          </ActionButton>
        </ButtonGroup>
      </ModalContent>
    </StyledModal>
  )
}
